import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import TitleIcon from '@mui/icons-material/Title';
import { Color } from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { FC, useEffect } from 'react';
import { useEditor, Tiptap, TiptapContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import { LeadMergeTagHighlight } from './LeadMergeTagHighlight';

type LeadEmailBodyEditorProps = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeightClass?: string;
  id?: string;
  variant?: 'default' | 'campaign';
};

/** Returns true when TipTap HTML has no meaningful text content. */
export const isLeadEmailBodyEmpty = (html: string): boolean => {
  const stripped = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return stripped.length === 0;
};

type ToolbarButtonProps = {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

type ToolbarSelectProps = {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
};

const ToolbarDivider = () => <span className="mx-0.5 h-6 w-px bg-[#E9E9E9]" aria-hidden />;

/** Compact icon button for the email body formatting toolbar. */
const ToolbarButton: FC<ToolbarButtonProps> = ({ label, active = false, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`flex h-8 w-8 items-center justify-center rounded-md border text-sm transition-colors ${
      active
        ? 'border-[#013847] bg-[#013847] text-white'
        : 'border-transparent bg-transparent text-[#737373] hover:border-[#E9E9E9] hover:bg-white hover:text-[#013847]'
    }`}
  >
    {children}
  </button>
);

/** Gmail-style dropdown for font size presets. */
const ToolbarSelect: FC<ToolbarSelectProps> = ({ label, value, options, onChange }) => (
  <select
    aria-label={label}
    title={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="h-8 max-w-[110px] rounded-md border border-transparent bg-transparent px-1 text-xs text-[#737373] hover:border-[#E9E9E9] hover:bg-white focus:border-[#5DB6CE] focus:outline-none"
  >
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

const FONT_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'normal', label: 'Normal' },
  { value: 'large', label: 'Large' },
  { value: 'xlarge', label: 'Huge' },
];

const TEXT_COLORS = ['#013847', '#2C3E50', '#DC2626', '#2563EB', '#0F973D', '#B45309'];

export const LEAD_EMAIL_IMAGE_PLACEHOLDERS = [
  { key: 'logo_url', label: 'Logo', token: '{{logo_url}}', preview: 'https://placehold.co/160x48/013847/FFFFFF?text=Logo' },
  { key: 'hero_image_url', label: 'Hero', token: '{{hero_image_url}}', preview: 'https://placehold.co/600x240/CFE8EF/013847?text=Hero+image' },
  { key: 'feature_image_url', label: 'Feature', token: '{{feature_image_url}}', preview: 'https://placehold.co/480x200/EFF8FA/013847?text=Feature' },
  { key: 'cta_image_url', label: 'CTA', token: '{{cta_image_url}}', preview: 'https://placehold.co/320x120/013847/FFFFFF?text=CTA' },
] as const;

/** Rich-text editor for staff-composed lead email bodies (HTML output). */
export const LeadEmailBodyEditor: FC<LeadEmailBodyEditorProps> = ({
  value,
  onChange,
  placeholder = 'Message body. Use {{first_name}} for personalization.',
  minHeightClass = 'min-h-[120px]',
  id,
  variant = 'default',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({ inline: true, allowBase64: false }),
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      LeadMergeTagHighlight,
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: `${minHeightClass} w-full px-3 py-2 font-open-sans text-sm text-[#2C3E50] focus:outline-none`,
        ...(id ? { id } : {}),
        'data-placeholder': placeholder,
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChange(currentEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const currentHtml = editor.getHTML();
    const normalizedValue = value || '';
    if (normalizedValue !== currentHtml && normalizedValue !== editor.getText()) {
      if (isLeadEmailBodyEmpty(normalizedValue) && isLeadEmailBodyEmpty(currentHtml)) return;
      editor.commands.setContent(normalizedValue, { emitUpdate: false });
    }
  }, [editor, value]);

  const handleSetLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', previousUrl ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleInsertImage = () => {
    if (!editor) return;
    const url = window.prompt('Image URL', 'https://');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const handleInsertPlaceholder = (token: string) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`<img src="${token}" alt="${token}" />`).run();
  };

  const applyFontSize = (sizeKey: string) => {
    if (!editor) return;
    if (sizeKey === 'xlarge') {
      editor.chain().focus().toggleHeading({ level: 2 }).run();
      return;
    }
    if (sizeKey === 'large') {
      editor.chain().focus().toggleHeading({ level: 3 }).run();
      return;
    }
    editor.chain().focus().setParagraph().run();
  };

  if (!editor) {
    return (
      <div
        className={`rounded-lg border border-[#E9E9E9] bg-[#FAFAFA] ${minHeightClass}`}
        aria-busy="true"
      />
    );
  }

  const toolbarClass =
    variant === 'campaign'
      ? 'flex flex-wrap items-center gap-0.5 border-b border-[#E9E9E9] bg-[#F1F3F4] px-2 py-1.5'
      : 'flex flex-wrap items-center gap-1 border-b border-[#E9E9E9] bg-[#FAFAFA] px-2 py-1.5';

  return (
    <Tiptap instance={editor}>
      <style>{`
        .lead-email-editor [data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #A3A3A3;
          pointer-events: none;
          float: left;
          height: 0;
        }
        .lead-email-editor .tiptap:focus { outline: none; }
        .lead-email-editor .tiptap h2 { font-size: 1.125rem; font-weight: 700; margin: 0.5rem 0; }
        .lead-email-editor .tiptap h3 { font-size: 1rem; font-weight: 600; margin: 0.5rem 0; }
        .lead-email-editor .tiptap ul, .lead-email-editor .tiptap ol { padding-left: 1.25rem; margin: 0.5rem 0; }
        .lead-email-editor .tiptap blockquote {
          border-left: 3px solid #E9E9E9;
          margin: 0.5rem 0;
          padding-left: 0.75rem;
          color: #737373;
        }
        .lead-email-editor .tiptap a { color: #013847; text-decoration: underline; }
        .lead-email-editor .lead-merge-tag {
          font-family: "Courier New", Courier, monospace;
          font-size: 0.92em;
          background: #F1F6F8;
          color: #013847;
          border-radius: 0.25rem;
          padding: 0.125rem 0.25rem;
          box-shadow: inset 0 0 0 1px rgba(1, 56, 71, 0.15);
        }
      `}</style>
      <div className="lead-email-editor overflow-hidden rounded-lg border border-[#E9E9E9] bg-white">
        <div className={toolbarClass}>
          <ToolbarSelect
            label="Font size"
            value="normal"
            options={FONT_SIZE_OPTIONS}
            onChange={applyFontSize}
          />
          <ToolbarDivider />
          <ToolbarButton
            label="Bold"
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBoldIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalicIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Underline"
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlinedIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Strikethrough"
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <FormatStrikethroughIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarDivider />
          <div className="flex items-center gap-0.5">
            <FormatColorTextIcon sx={{ fontSize: 16, color: '#737373' }} />
            {TEXT_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={`Text color ${color}`}
                title={`Text color ${color}`}
                className="h-5 w-5 rounded-full border border-[#E9E9E9]"
                style={{ backgroundColor: color }}
                onClick={() => editor.chain().focus().setColor(color).run()}
              />
            ))}
          </div>
          <ToolbarDivider />
          <ToolbarButton
            label="Align left"
            active={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <FormatAlignLeftIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Align center"
            active={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <FormatAlignCenterIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Align right"
            active={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <FormatAlignRightIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Justify"
            active={editor.isActive({ textAlign: 'justify' })}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          >
            <FormatAlignJustifyIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton
            label="Heading"
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <TitleIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Bullet list"
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulletedIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Numbered list"
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FormatListNumberedIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            label="Blockquote"
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <FormatQuoteIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          <ToolbarButton label="Link" active={editor.isActive('link')} onClick={handleSetLink}>
            <LinkIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
          {variant === 'campaign' ? (
            <>
              <ToolbarButton label="Insert image URL" onClick={handleInsertImage}>
                <ImageIcon sx={{ fontSize: 18 }} />
              </ToolbarButton>
              <ToolbarSelect
                label="Insert placeholder image"
                value=""
                options={[
                  { value: '', label: 'Placeholder…' },
                  ...LEAD_EMAIL_IMAGE_PLACEHOLDERS.map((item) => ({
                    value: item.key,
                    label: item.label,
                  })),
                ]}
                onChange={(value) => {
                  const placeholder = LEAD_EMAIL_IMAGE_PLACEHOLDERS.find((item) => item.key === value);
                  if (placeholder) handleInsertPlaceholder(placeholder.token);
                }}
              />
            </>
          ) : null}
          <ToolbarButton
            label="Clear formatting"
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          >
            <FormatClearIcon sx={{ fontSize: 18 }} />
          </ToolbarButton>
        </div>
        <TiptapContent className={`tiptap ${minHeightClass}`} />
      </div>
    </Tiptap>
  );
};
